import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  OnInit,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ENVIRONMENT_PLATFORM_TOKEN, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { BasicLoginCredentials, isUserType, UserType } from '@campus/public-portal';
import { StringFunctions } from '@campus/utils';
import '@diekeure/dcr-components/dcr-dialog.js';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { REDIRECT_AFTER_LOGIN_PARAM } from '../../services/redirect.service';
import { LoginPageContainerViewModel } from './login-page-container.viewmodel';

type ModeType = 'basic-login' | 'forgot-password' | 'sso';

@Component({
  standalone: false,
  selector: 'polpo-login-page-container',
  templateUrl: './login-page-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LoginPageContainerViewModel],
})
export class LoginPageContainerComponent implements OnInit {
  private vm = inject(LoginPageContainerViewModel);
  private environmentWebsite = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private platform = inject(ENVIRONMENT_PLATFORM_TOKEN);
  private destroyRef = inject(DestroyRef);

  private queryParams: Signal<Params> = toSignal(this.route.queryParams, { initialValue: {} as Params });

  // Writable so setForgotPasswordMode/setLoginMode can override it, but it resets to the
  // params-derived mode whenever the query params change.
  public readonly mode = linkedSignal<ModeType>(() => (this.queryParams().provider ? 'sso' : 'basic-login'));

  public readonly provider: Signal<string> = computed(() => {
    const { provider } = this.queryParams();
    if (provider === 'acm') return 'leerId'; // only students are allowed to create new accounts
    return provider;
  });

  public passwordResetRequested$: Observable<boolean>;
  public loginError$: Observable<string> = this.vm.error$;
  public showEmailSentDialog$ = new BehaviorSubject<boolean>(false);

  public isMaintenanceMode$: Observable<boolean>;
  public statusPageUrl: string;
  public ssoError$: Observable<string> = this.vm.error$.pipe(filter((error) => !error || this.mode() === 'sso'));
  public role$ = this.vm.role$;

  public ngOnInit(): void {
    this.isMaintenanceMode$ = this.vm.isMaintenanceMode$;
    this.statusPageUrl = this.environmentWebsite.statusPageUrl;

    // Side effects only — subscribed here (not through the template) so they never run inside a change detection pass.
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const role: UserType = params.provider ? 'student' : this.getDefaultRole(params);
      this.vm.setRole(role);

      const as = 'Aanmelden' + (role === 'schooladmin' ? ' als beheerder' : '');
      this.title.setTitle(`${StringFunctions.capitalizeFirstLetter(this.platform)} - ${as}`);
    });
  }

  private getDefaultRole(params: Params): UserType | null {
    if (params.role && isUserType(params.role)) {
      return params.role;
    }

    return null;
  }

  public setForgotPasswordMode() {
    this.mode.set('forgot-password');
  }

  public setLoginMode() {
    this.passwordResetRequested$ = null;
    this.mode.set('basic-login');

    // clear query params, but keep where the user has to end up after login
    const queryParams = { ...this.route.snapshot?.queryParams };
    Object.keys(queryParams)
      .filter((key) => key !== REDIRECT_AFTER_LOGIN_PARAM)
      .forEach((key) => {
        queryParams[key] = null;
      });
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge', relativeTo: this.route });
  }

  public resetPassword(email: string) {
    this.vm
      .resetPassword(email)
      .pipe(
        take(1),
        tap((success) => {
          if (success) {
            this.showEmailSentDialog$.next(true);
          }
        })
      )
      .subscribe();
  }

  public onEmailSentDialogConfirm() {
    this.showEmailSentDialog$.next(false);
    this.setLoginMode();
  }

  public loginWithCredentials(credentials: BasicLoginCredentials) {
    this.vm.loginWithCredentials(credentials);
  }

  public loginWith(provider: string) {
    this.vm.loginWithProvider(provider);
  }
}
